import type { Question } from './topics';
import { shuffle } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// The Custom Lesson Builder: questions the teacher writes, each in a format
// they choose. Unlike the generated topics, a builder question keeps its
// format at every difficulty level, because the teacher picked it on purpose.
// ─────────────────────────────────────────────────────────────────────────────

export type BuilderKind = 'choice' | 'blank' | 'short' | 'truefalse' | 'translate' | 'order';

export type BuilderItem = {
  id: string;
  kind: BuilderKind;
  /** The question, sentence with ___, statement, sentence to translate, or (order) a clue. */
  prompt: string;
  /** Right answer. Alternatives separated by " / ". For true/false: 'true' or 'false'. For order: the sentence. */
  answer: string;
  /** Multiple choice: the wrong options. */
  wrong?: string[];
  /** Build the sentence: extra decoy words, space-separated. */
  extra?: string;
  /** Shown after answering. */
  why?: string;
};

export const BUILDER_KINDS: Record<BuilderKind, { name: string; icon: string; blurb: string }> = {
  choice: { name: 'Multiple choice', icon: '🔘', blurb: 'Pick the right answer from options you write' },
  blank: { name: 'Fill in the blank', icon: '✏️', blurb: 'Type the missing word in a sentence' },
  short: { name: 'Short answer', icon: '⌨️', blurb: 'Type the answer to a question' },
  truefalse: { name: 'True or false', icon: '⚖️', blurb: 'Decide if a statement is right' },
  translate: { name: 'Translate', icon: '🔁', blurb: 'Type a translation of a sentence' },
  order: { name: 'Build the sentence', icon: '🧱', blurb: 'Tap word tiles into the right order' },
};
export const BUILDER_ORDER: BuilderKind[] = ['choice', 'blank', 'short', 'truefalse', 'translate', 'order'];

const uid = () => Math.random().toString(36).slice(2, 10);

export function newItem(kind: BuilderKind): BuilderItem {
  return {
    id: uid(), kind, prompt: '', answer: kind === 'truefalse' ? 'true' : '',
    ...(kind === 'choice' ? { wrong: ['', ''] } : {}),
  };
}

/** Switch a question to another format, keeping what still fits. */
export function convertItem(item: BuilderItem, kind: BuilderKind): BuilderItem {
  const keepAnswer = kind === 'truefalse'
    ? (item.answer === 'false' ? 'false' : 'true')
    : (item.kind === 'truefalse' ? '' : item.answer);
  return {
    id: item.id, kind, prompt: item.prompt, answer: keepAnswer, why: item.why,
    ...(kind === 'choice' ? { wrong: item.wrong?.length ? item.wrong : ['', ''] } : {}),
    ...(kind === 'order' && item.extra ? { extra: item.extra } : {}),
  };
}

export const alternatives = (s: string) => s.split('/').map(x => x.trim()).filter(Boolean);

/** Word tiles for a sentence: its words without the punctuation stuck to them. */
export const tilesOf = (s: string) =>
  s.split(/\s+/).map(w => w.replace(/^[¿¡"“«(]+|[.,;:!?"”»)؟،]+$/g, '')).filter(Boolean);

/** What's missing from a question, in plain words; empty = ready. */
export function itemProblem(it: BuilderItem): string | null {
  const p = it.prompt.trim();
  switch (it.kind) {
    case 'choice':
      if (!p) return 'Write the question.';
      if (!it.answer.trim()) return 'Write the right answer.';
      if (!(it.wrong ?? []).some(w => w.trim())) return 'Add at least one wrong answer.';
      if ((it.wrong ?? []).some(w => w.trim() && w.trim() === it.answer.trim())) return 'A wrong answer is the same as the right one.';
      return null;
    case 'blank':
      if (!p.includes('___')) return 'Put ___ where the blank goes.';
      if (!alternatives(it.answer).length) return 'Write the missing word.';
      return null;
    case 'short':
      if (!p) return 'Write the question.';
      if (!alternatives(it.answer).length) return 'Write the answer.';
      return null;
    case 'truefalse':
      return p ? null : 'Write the statement.';
    case 'translate':
      if (!p) return 'Write the sentence to translate.';
      if (!alternatives(it.answer).length) return 'Write the translation.';
      return null;
    case 'order':
      if (tilesOf(it.answer).length < 2) return 'Write a sentence of at least two words.';
      return null;
  }
}

/** A builder question as a lesson question. Fixed: difficulty never changes its format. */
export function toQuestion(it: BuilderItem): Question | null {
  if (itemProblem(it)) return null;
  const base = { key: `custom:${it.id}`, topic: 'custom' as const, fixed: true, ...(it.why?.trim() ? { why: it.why.trim() } : {}) };
  const prompt = it.prompt.trim();
  switch (it.kind) {
    case 'choice': {
      const answer = it.answer.trim();
      const wrong = [...new Set((it.wrong ?? []).map(w => w.trim()).filter(w => w && w !== answer))];
      return { ...base, kind: 'choice', instruction: 'Choose the answer', prompt, answers: [answer], options: shuffle([answer, ...wrong]) };
    }
    case 'blank':
      return { ...base, kind: 'type', instruction: 'Fill in the blank', prompt, answers: alternatives(it.answer) };
    case 'short':
      return { ...base, kind: 'type', instruction: 'Write the answer', prompt, answers: alternatives(it.answer) };
    case 'truefalse':
      return {
        ...base, kind: 'choice', instruction: 'True or false?', prompt,
        answers: [it.answer === 'false' ? 'False' : 'True'], options: ['True', 'False'],
      };
    case 'translate':
      return { ...base, kind: 'type', instruction: 'Translate this sentence', prompt, answers: alternatives(it.answer) };
    case 'order': {
      const sentences = alternatives(it.answer);
      const tiles = [...tilesOf(sentences[0]), ...tilesOf(it.extra ?? '')];
      return {
        ...base, kind: 'order', instruction: 'Build the sentence', prompt: prompt || 'Put the words in order',
        answers: sentences, tiles: shuffle(tiles),
      };
    }
  }
}

/** Old "question = answer | wrong" lines, as builder questions. */
export function fromLegacy(lines: { prompt: string; answers: string[]; wrong: string[] }[]): BuilderItem[] {
  return lines.map(l => (l.wrong.length
    ? { id: uid(), kind: 'choice' as const, prompt: l.prompt, answer: l.answers[0], wrong: l.wrong }
    : { id: uid(), kind: (l.prompt.includes('___') ? 'blank' : 'short') as BuilderKind, prompt: l.prompt, answer: l.answers.join(' / ') }));
}
